/**
 * The customisable dashboard grid: every block on the page is a card on a 12-column tile grid,
 * and in Customize mode the operator arranges them.
 *
 * ── The grid ──
 *
 * 316 px rows, 14 px gaps, `grid-auto-flow: row dense` so a shrunk card fills the hole beside
 * an expanded one rather than starting a new row. Every card has two sizes, and an expanded
 * card one of two orientations:
 *
 *   shrink              4 columns × 1 row       — the headline and its chart
 *   expand, horizontal  8 or 12 columns × 1 row — the shrunk card, with the full list BESIDE it
 *   expand, vertical    4 columns × 2 rows      — the summary, with the full list UNDER it
 *
 * Horizontal widens a card and never makes it taller: the summary sits on the left and the list
 * takes the rest of the row. The operator picks two thirds or the full board width per card.
 * Vertical is the opposite trade — the card keeps a shrunk card's width and grows down, and its
 * list (rows or picture tiles) scrolls vertically.
 * At full width the summary keeps a shrunk card's exact width (`summaryWidth`); at two thirds it
 * narrows to three columns' worth (never under 260 px), because the list beside it has only
 * four columns of room otherwise. A card may still override its span (`def.span`).
 * Below 980 px of board width shrunk cards are 6 columns and horizontal ones 12 (vertical ones
 * keep 6); below 640 px every card is full width and an expanded card is always vertical,
 * because there is no room beside it.
 * The width measured is the BOARD's, not the window's — the sidebar collapsing changes the
 * room the cards have without the window changing at all.
 *
 * ── Customize mode ──
 *
 * Each card grows a toolbar: drag handle, ◀ ▶, Shrink | Expand, and when expanded the
 * orientation, ⅔ | Full (horizontal only) and List | Cards; then settings, hide. It wraps to a
 * second line on a card too narrow for it rather than clipping a control off the end. Size and view are ONLY changeable here; outside it a card shows
 * its saved choice, so a mis-click while reading never rearranges the board.
 */
import { useState, type ComponentType, type DragEvent, type ReactNode } from 'react';
import {
  AdjustmentsHorizontalIcon,
  ArrowsPointingOutIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  EyeSlashIcon,
  ListBulletIcon,
  PlusIcon,
  RectangleStackIcon,
  Squares2X2Icon,
  ViewColumnsIcon,
} from '@heroicons/react/24/outline';
import { BOARD_RANGES, type BoardLayout, type BoardLayoutItem, type BoardOrient, type BoardRange, type BoardSpan } from './boardLayout';
import { BoardCardContext, type BoardCardState, type BoardFilter, FOCUS, SUNK, tint } from './BoardParts';
import { useElementSize } from './useElementSize';

export type BoardWidth = 'narrow' | 'mid' | 'wide';

export interface BoardCardDef {
  id: string;
  title: string;
  icon: ComponentType<{ className?: string }>;
  /** Offer List | Cards for the expanded details. Default true. */
  views?: boolean;
  /** Offer the time-range setting. Default false — presence and live meters have no window. */
  ranged?: boolean;
  /** A non-standard span for some state, or null for the default. Only consulted on a wide board. */
  span?: (state: BoardCardState) => { cols: number; rows: number } | null;
  /** The card's contents. State arrives through `useBoardCard()`. */
  render: () => ReactNode;
}

export interface DashboardBoardProps {
  cards: BoardCardDef[];
  layout: BoardLayout;
  editing: boolean;
  /** Shown when every card is hidden or none are available. */
  empty?: ReactNode;
}

const brandSoft = 'var(--brand-light)';
const brandLine = tint('var(--brand)', 45);

const GAP = 14;

function spanFor(def: BoardCardDef, state: BoardCardState, span: BoardSpan, orient: BoardOrient, width: BoardWidth) {
  const expanded = state.size === 'expand';
  if (width === 'narrow') return { cols: 12, rows: expanded ? 2 : 1 };
  // Vertical: a shrunk card's width, two rows tall.
  if (expanded && orient === 'vertical') return { cols: width === 'mid' ? 6 : 4, rows: 2 };
  if (width === 'mid') return { cols: expanded ? 12 : 6, rows: 1 };
  return def.span?.(state) || { cols: expanded ? (span === 'full' ? 12 : 8) : 4, rows: 1 };
}

/** Inner width in px of a card `cols` columns wide on a board `px` wide. */
function colsWidth(px: number, cols: number) {
  const cell = (px - 11 * GAP) / 12;
  // Less the card's 1 px borders, so the content box matches a card of that span exactly.
  return Math.max(0, Math.floor(cols * cell + (cols - 1) * GAP - 2));
}

/** The summary half of a split card: a shrunk card's width, or narrower in a two-thirds card. */
function summaryWidthFor(px: number, width: BoardWidth, span: BoardSpan) {
  const shrunk = colsWidth(px, width === 'mid' ? 6 : 4);
  if (width !== 'wide' || span === 'full') return shrunk;
  return Math.min(shrunk, Math.max(260, colsWidth(px, 3)));
}

/**
 * Calls a card's `render` as a COMPONENT, below the context provider: a card may use hooks and
 * `useBoardCard()` in its render, which would otherwise run in the board's own hook order.
 */
function CardContent({ render }: { render: () => ReactNode }) {
  return <>{render()}</>;
}

/** Six dots — the drag affordance. */
function GripIcon({ className = '' }: { className?: string }) {
  return (
    <svg viewBox="0 0 10 16" className={className} fill="currentColor" aria-hidden>
      {[3, 8, 13].flatMap((y) => [2.5, 7.5].map((x) => <circle key={`${x}-${y}`} cx={x} cy={y} r={1.4} />))}
    </svg>
  );
}

function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  /** An option with an icon and an empty label is icon-only; its `title` names it. */
  options: Array<{ value: T; label: string; title?: string; icon?: ComponentType<{ className?: string }> }>;
  onChange: (v: T) => void;
  label: string;
}) {
  return (
    <span role="group" aria-label={label} className="flex flex-none rounded-[7px] border border-border bg-card p-[2px]">
      {options.map((o) => {
        const on = o.value === value;
        const Icon = o.icon;
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={on}
            title={o.title}
            aria-label={o.label || o.title}
            onClick={() => onChange(o.value)}
            className={`flex h-[22px] items-center gap-1 whitespace-nowrap rounded-[5px] ${o.label ? 'px-2' : 'px-1.5'} text-[11px] font-semibold transition-colors ${FOCUS} ${
              on ? 'bg-brand text-white' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {Icon && <Icon className={o.label ? 'h-3 w-3' : 'h-3.5 w-3.5'} />}
            {o.label}
          </button>
        );
      })}
    </span>
  );
}

function ToolButton({ label, onClick, disabled, active, children }: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  active?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      className={`flex h-[26px] w-[26px] flex-none items-center justify-center rounded-[6px] border border-border bg-card transition-colors disabled:opacity-40 ${FOCUS} ${
        active ? 'text-brand' : 'text-muted-foreground enabled:hover:text-foreground'
      }`}
    >
      {children}
    </button>
  );
}

function CardToolbar({
  def,
  item,
  width,
  first,
  last,
  layout,
  settingsOpen,
  onToggleSettings,
}: {
  def: BoardCardDef;
  item: BoardLayoutItem;
  width: BoardWidth;
  first: boolean;
  last: boolean;
  layout: BoardLayout;
  settingsOpen: boolean;
  onToggleSettings: () => void;
}) {
  const expanded = item.size === 'expand';
  return (
    <div
      className="flex flex-none flex-wrap items-center gap-x-1.5 gap-y-1 px-2 py-[5px]"
      style={{ backgroundColor: brandSoft, borderBottom: `1px dashed ${brandLine}` }}
    >
      <span className="flex-none cursor-grab text-brand active:cursor-grabbing" title="Drag to move">
        <GripIcon className="h-4 w-2.5" />
      </span>
      <span className="min-w-[72px] flex-1 truncate text-[12px] font-semibold text-brand" title={def.title}>
        {def.title}
      </span>
      <ToolButton label="Move earlier" onClick={() => layout.move(item.id, -1)} disabled={first}>
        <ChevronLeftIcon className="h-3.5 w-3.5" />
      </ToolButton>
      <ToolButton label="Move later" onClick={() => layout.move(item.id, 1)} disabled={last}>
        <ChevronRightIcon className="h-3.5 w-3.5" />
      </ToolButton>
      <Segmented
        label="Card size"
        value={item.size}
        options={[{ value: 'shrink', label: 'Shrink' }, { value: 'expand', label: 'Expand' }]}
        onChange={(size) => layout.update(item.id, { size })}
      />
      {expanded && width !== 'narrow' && (
        <Segmented
          label="List position"
          value={item.orient}
          options={[
            { value: 'horizontal', label: '', title: 'Horizontal — list beside the card', icon: ViewColumnsIcon },
            { value: 'vertical', label: '', title: 'Vertical — list below the card', icon: RectangleStackIcon },
          ]}
          onChange={(orient) => layout.update(item.id, { orient })}
        />
      )}
      {expanded && width === 'wide' && item.orient === 'horizontal' && (
        <Segmented
          label="Expanded width"
          value={item.span}
          options={[{ value: 'two-thirds', label: '⅔' }, { value: 'full', label: 'Full' }]}
          onChange={(span) => layout.update(item.id, { span })}
        />
      )}
      {expanded && def.views !== false && (
        <Segmented
          label="Details view"
          value={item.view}
          options={[
            { value: 'list', label: 'List', icon: ListBulletIcon },
            { value: 'cards', label: 'Cards', icon: Squares2X2Icon },
          ]}
          onChange={(view) => layout.update(item.id, { view })}
        />
      )}
      <ToolButton label="Card settings" onClick={onToggleSettings} active={settingsOpen}>
        <AdjustmentsHorizontalIcon className="h-3.5 w-3.5" />
      </ToolButton>
      <ToolButton label="Hide card" onClick={() => layout.update(item.id, { hidden: true })}>
        <EyeSlashIcon className="h-3.5 w-3.5" />
      </ToolButton>
    </div>
  );
}

function SettingsRow({ def, item, layout }: { def: BoardCardDef; item: BoardLayoutItem; layout: BoardLayout }) {
  return (
    <div className="flex flex-none flex-wrap items-center gap-x-4 gap-y-1.5 border-b border-border px-3 py-2 text-[12px]" style={{ backgroundColor: SUNK }}>
      <label className="flex cursor-pointer items-center gap-1.5 text-foreground">
        <input
          type="checkbox"
          checked={item.showMeta}
          onChange={(e) => layout.update(item.id, { showMeta: e.target.checked })}
          className={`h-3.5 w-3.5 accent-[var(--brand)] ${FOCUS}`}
        />
        Show metadata &amp; captions
      </label>
      {def.ranged && (
        <label className="flex items-center gap-1.5 text-muted-foreground">
          Time range
          <select
            value={item.range}
            onChange={(e) => layout.update(item.id, { range: e.target.value as BoardRange })}
            className={`h-[26px] rounded-md border border-border bg-card px-1.5 text-[12px] text-foreground ${FOCUS}`}
          >
            {BOARD_RANGES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </label>
      )}
      <span className="text-[11px] text-muted-foreground">
        {def.ranged ? 'The range applies to this card only.' : 'This card always shows the current state.'}
      </span>
    </div>
  );
}

export default function DashboardBoard({ cards, layout, editing, empty }: DashboardBoardProps) {
  const [sizeRef, { width: px }] = useElementSize<HTMLDivElement>();
  const width: BoardWidth = px > 0 && px < 640 ? 'narrow' : px > 0 && px < 980 ? 'mid' : 'wide';
  const [settingsId, setSettingsId] = useState<string | null>(null);
  // Each card's list filter, picked from its summary. Not saved with the layout — a filter is a
  // question asked of today's list, not an arrangement of the board — and ignored while the
  // card is shrunk, so shrinking a card and expanding it again starts from the whole list.
  const [filters, setFilters] = useState<Record<string, BoardFilter | null>>({});
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);

  const defs = new Map(cards.map((c) => [c.id, c]));
  const placed = layout.items.filter((it) => defs.has(it.id));
  const visible = placed.filter((it) => !it.hidden);
  const hidden = placed.filter((it) => it.hidden);

  const onDragStart = (id: string) => (e: DragEvent) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = 'move';
    // Firefox will not start a drag without data.
    try { e.dataTransfer.setData('text/plain', id); } catch { /* not needed elsewhere */ }
  };
  const onDragOver = (id: string) => (e: DragEvent) => {
    if (!dragId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (overId !== id) setOverId(id);
  };
  const onDrop = (id: string) => (e: DragEvent) => {
    e.preventDefault();
    if (dragId) layout.moveTo(dragId, id);
    setDragId(null);
    setOverId(null);
  };
  const onDragEnd = () => {
    setDragId(null);
    setOverId(null);
  };

  return (
    <div className="space-y-[14px]">
      {editing && (
        <div
          className="flex flex-wrap items-start gap-3 rounded-xl px-4 py-3 text-[13px] leading-snug text-foreground"
          style={{ backgroundColor: brandSoft, border: `1px dashed ${brandLine}` }}
        >
          <ArrowsPointingOutIcon className="mt-0.5 h-4 w-4 flex-none text-brand" aria-hidden />
          <p className="min-w-0 flex-1">
            <strong className="font-semibold">Customizing layout.</strong> Drag a card to move it. Every card has two
            sizes — Shrink (one tile) and Expand, which adds the full list either beside the tile (horizontal, two
            thirds or the full width of the board) or below it (vertical, a tile wide and twice as tall). Expand the
            cards that matter most. Changes save on this browser.
          </p>
          {hidden.length > 0 && (
            <div className="flex w-full flex-wrap items-center gap-1.5">
              <span className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Hidden</span>
              {hidden.map((it) => (
                <button
                  key={it.id}
                  type="button"
                  onClick={() => layout.update(it.id, { hidden: false })}
                  title="Show this card again"
                  className={`flex items-center gap-1 rounded-full border border-border bg-card px-2.5 py-0.5 text-[12px] font-medium text-foreground hover:text-brand ${FOCUS}`}
                >
                  <PlusIcon className="h-3 w-3" />
                  {defs.get(it.id)!.title}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div
        data-tour="dashboard-board-cards"
        ref={sizeRef}
        className="grid grid-cols-12 gap-[14px]"
        style={{ gridAutoRows: '316px', gridAutoFlow: 'row dense' }}
      >
        {visible.map((item, i) => {
          const def = defs.get(item.id)!;
          const state: BoardCardState = {
            id: item.id,
            size: item.size,
            view: def.views === false ? 'list' : item.view,
            showMeta: item.showMeta,
            range: def.ranged ? item.range : 'today',
            editing,
            split: item.size === 'expand' && width !== 'narrow' && item.orient === 'horizontal',
            filter: item.size === 'expand' ? filters[item.id] || null : null,
            setFilter: (f) => setFilters((cur) => ({ ...cur, [item.id]: f })),
            summaryWidth: px > 0 ? summaryWidthFor(px, width, item.span) : undefined,
          };
          const { cols, rows } = spanFor(def, state, item.span, item.orient, width);
          const isOver = editing && overId === item.id && dragId !== item.id;
          return (
            <section
              key={item.id}
              aria-label={def.title}
              draggable={editing}
              onDragStart={editing ? onDragStart(item.id) : undefined}
              onDragOver={editing ? onDragOver(item.id) : undefined}
              onDrop={editing ? onDrop(item.id) : undefined}
              onDragEnd={editing ? onDragEnd : undefined}
              className="flex min-h-0 min-w-0 flex-col overflow-hidden rounded-2xl border bg-card shadow-card transition-[box-shadow,opacity,border-color] duration-150 hover:shadow-[0_6px_22px_rgba(16,24,40,0.09)]"
              style={{
                gridColumn: `span ${cols} / span ${cols}`,
                gridRow: `span ${rows} / span ${rows}`,
                borderColor: isOver ? 'var(--brand)' : editing ? brandLine : 'var(--border)',
                boxShadow: isOver ? `0 0 0 4px ${brandSoft}` : undefined,
                opacity: dragId === item.id ? 0.45 : 1,
              }}
            >
              {editing && (
                <CardToolbar
                  def={def}
                  item={item}
                  width={width}
                  first={i === 0}
                  last={i === visible.length - 1}
                  layout={layout}
                  settingsOpen={settingsId === item.id}
                  onToggleSettings={() => setSettingsId((cur) => (cur === item.id ? null : item.id))}
                />
              )}
              {editing && settingsId === item.id && <SettingsRow def={def} item={item} layout={layout} />}
              <BoardCardContext.Provider value={state}>
                <CardContent render={def.render} />
              </BoardCardContext.Provider>
            </section>
          );
        })}
      </div>

      {visible.length === 0 && (
        <div className="rounded-2xl border border-dashed border-border bg-card px-5 py-8 text-center text-[13px] text-muted-foreground">
          {hidden.length > 0 && !editing
            ? 'Every card is hidden — use Customize layout to bring them back.'
            : empty || 'Nothing to show on this dashboard.'}
        </div>
      )}
    </div>
  );
}
